import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Mail, ArrowLeft } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { api } from '@/lib/api'
import { AuthAlert } from '@/components/auth/AuthAlert'
import { AuthSubmitButton } from '@/components/auth/AuthSubmitButton'

function formatApiError(err: any): string {
  const detail = err?.response?.data?.detail
  if (typeof detail === 'string') return detail
  if (Array.isArray(detail)) {
    return detail.map((item: any) => item.msg || JSON.stringify(item)).join('. ')
  }
  if (detail && typeof detail === 'object') {
    return detail.msg || JSON.stringify(detail)
  }
  return 'Failed to send reset link. Please try again.'
}

export function ForgotPassword() {
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState('')
  const [touched, setTouched] = useState(false)

  const emailError = useMemo(() => {
    if (!email) return 'Email is required'
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return 'Enter a valid email address'
    return ''
  }, [email])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setTouched(true)
    if (emailError) return

    setLoading(true)
    setError('')
    try {
      await api.post('/auth/forgot-password', { email })
      setSent(true)
    } catch (err: any) {
      setError(formatApiError(err))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="text-center space-y-1.5">
        <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
          <Mail className="h-5 w-5 text-primary" />
        </div>
        <h1 className="text-2xl font-bold tracking-tight">Reset Password</h1>
        <p className="text-sm text-muted-foreground">Enter your email and we'll send you a reset link</p>
      </div>

      {sent ? (
        <div className="space-y-4">
          <AuthAlert
            message="If an account exists with this email, a reset link has been sent."
            variant="success"
          />
          <Button asChild variant="outline" className="w-full">
            <Link to="/login">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Login
            </Link>
          </Button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && <AuthAlert message={error} />}

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={() => setTouched(true)}
              aria-invalid={touched && !!emailError}
              aria-describedby={touched && emailError ? 'email-error' : undefined}
              required
              autoFocus
            />
            {touched && emailError && (
              <p id="email-error" className="text-xs text-destructive">{emailError}</p>
            )}
          </div>

          <AuthSubmitButton loading={loading} disabled={!email || !!emailError}>
            Send Reset Link
          </AuthSubmitButton>

          <div className="text-center">
            <Link to="/login" className="text-sm text-muted-foreground hover:text-primary transition-colors">
              Remember your password? Sign in
            </Link>
          </div>
        </form>
      )}
    </div>
  )
}
